import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb } from "lucide-react";
import { Button } from "./ui/button";
import { Seo } from "./Seo";
import { Faq } from "./Faq";

export function ArticleLayout({ seoTitle, seoDesc, path, faqs, image, eyebrow, title, intro, sections, ctaTitle, ctaText, diagram }) {
  return (
    <>
      <Seo title={seoTitle} path={path} description={seoDesc} faq={faqs} image={image} />

      <section className="relative overflow-hidden border-b border-border">
        <div className="drafting-grid absolute inset-0 opacity-30" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-20">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">{eyebrow}</span>
            <h1 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">{title}</h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">{intro}</p>
          </div>
          <div className="rotate-1 rounded-2xl border-[10px] border-white bg-white p-1 shadow-xl ring-1 ring-border">
            <img src={image} alt={title} className="w-full rounded-lg object-cover" style={{ aspectRatio: "3/2" }} />
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
        {sections.map((s, i) => (
          <section key={i} className="mb-12">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">{s.heading}</h2>
            {s.paragraphs?.map((p, j) => (
              <p key={j} className="mt-4 text-[16px] leading-relaxed text-foreground/85">{p}</p>
            ))}
            {s.list && (
              <ul className="mt-4 space-y-2.5">
                {s.list.map((li, k) => (
                  <li key={k} className="flex items-start gap-2.5 text-[16px] leading-relaxed text-foreground/85">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> <span>{li}</span>
                  </li>
                ))}
              </ul>
            )}
            {s.tip && (
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
                <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <p className="text-[15px] leading-relaxed text-foreground/85"><span className="font-semibold">Tip:</span> {s.tip}</p>
              </div>
            )}
          </section>
        ))}
      </article>

      {diagram && (
        <figure className="mx-auto max-w-4xl px-4 pb-6 sm:px-6" data-testid="article-diagram">
          <div className="overflow-hidden rounded-2xl border border-border bg-white p-4 shadow-sm sm:p-6">
            <img src={diagram.src} alt={diagram.alt} className="mx-auto w-full max-w-3xl" loading="lazy" />
          </div>
          {diagram.caption && (
            <figcaption className="mt-3 text-center text-sm text-muted-foreground">{diagram.caption}</figcaption>
          )}
        </figure>
      )}

      <Faq items={faqs} />

      <section className="border-t border-border bg-primary">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 py-12 text-center sm:px-6 lg:flex-row lg:px-8 lg:text-left">
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-primary-foreground sm:text-3xl">{ctaTitle}</h2>
            {ctaText && <p className="mt-2 max-w-2xl text-primary-foreground/80">{ctaText}</p>}
          </div>
          <div className="flex shrink-0 gap-3">
            <Button asChild size="lg" variant="secondary" className="gap-2" data-testid="article-cta">
              <Link to="/calculator">Open the calculator <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 bg-transparent text-primary-foreground hover:bg-white/10 hover:text-primary-foreground" data-testid="article-cta-pricing">
              <Link to="/pricing">See plans</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
